import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source'
import { defineStore } from 'pinia'
import combineURLs from '@/utils/combineURLs'

interface EventsStore {
  events: Event[]
}

interface Event {
  type: EventType
  payload?: any
}

export enum EventType {
  OrderValidated = 'OrderValidated',
}

const useEventsStore = defineStore({
  id: 'events',

  state: (): EventsStore => ({
    events: [],
  }),

  actions: {
    add(event: Event) {
      this.events.push(event)
    },
  },
})

class RetriableError extends Error {}
class FatalError extends Error {}

export default function () {
  const store = useEventsStore()

  function openConnection() {
    const url = combineURLs(import.meta.env.VITE_NEST_API_URL, 'shops/events')
    const token = (window as any)?.xprops?.context?.mbo_token

    let countError = 0
    fetchEventSource(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      async onopen(response) {
        if (
          response.ok &&
          response.headers.get('content-type') === EventStreamContentType
        ) {
          return // everything's good
        } else if (
          response.status >= 400 &&
          response.status < 500 &&
          response.status !== 429
        ) {
          // client-side errors are usually non-retriable:
          throw new FatalError()
        } else {
          throw new RetriableError()
        }
      },
      onmessage(message) {
        countError = 0
        if ((<any>Object).values(EventType).includes(message.event)) {
          store.add({
            type: message.event as EventType,
            payload: JSON.parse(message.data),
          })
        }
      },
      onerror(err) {
        if (err instanceof FatalError) {
          throw err // rethrow to stop the operation
        } else if (countError >= 10) {
          throw err // rethrow to stop the operation
        } else {
          countError++
        }
      },
    })
  }

  function onEvent(eventType: EventType, callback: (event: Event) => void) {
    const unsubscribe = store.$onAction(({ args }) => {
      const [event] = args
      if (event.type === eventType) {
        callback(event)
      }
    })

    return unsubscribe
  }

  return { openConnection, onEvent }
}
